import { NgModule } from '@angular/core';
import { RouteReuseStrategy, RouterModule, Routes, TitleStrategy } from '@angular/router';
import { MasterDetailRouteReuseStrategy } from './master-detail-route-reuse-strategy';
import { CatchAllComponent } from './catchall.component';
import { AuthGuard } from './core/guards/auth.guard';
import { WelcomeComponent } from './welcome.component';
import { ComplianceGuard } from './core/guards/compliance.guard';
import { InAppNotificationsTabsComponent } from 'src/app/in-app-notifications/in-app-notification-tabs/in-app-notifications-tabs.component';
import { DatadogLoggerComponent } from './datadog-logger/datadog-logger.component';
import { CustomTitleStrategy } from './page-title/custom-title-strategy';
import { PARTNER_NETWORK_PROVIDERS } from 'src/app/partner-network/partner-network-routing.module';

const appRoutes: Routes = [
    {
        path: 'welcome/home',
        canActivate: [AuthGuard],
        component: WelcomeComponent,
        title: 'Home'
    },
    {
        path: 'devops/logger',
        component: DatadogLoggerComponent
    },
    {
        path: 'in-app-notifications',
        canActivate: [AuthGuard],
        component: InAppNotificationsTabsComponent
    },
    {
        path: 'delegations',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/delegations/delegations.routes').then((m) => m.DelegationsRoutes)
    },
    {
        path: 'admin',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/admin/admin.module').then((m) => m.AdminModule)
    },
    {
        path: 'accountmanagers',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/accountmanagers/accountmanagers.module').then((m) => m.AccountManagersModule)
    },
    {
        path: 'billing',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/billing/billing.module').then((m) => m.BillingModule)
    },
    {
        path: 'bizdev',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/bizdev/bizdev.module').then((m) => m.BizDevModule)
    },
    {
        path: 'compliance',
        canActivate: [AuthGuard],
        canLoad: [ComplianceGuard],
        loadChildren: () => import('src/app/compliance/compliance.module').then((m) => m.ComplianceModule)
    },
    {
        path: 'credentialing',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/credentialing/credentialing.module').then((m) => m.CredentialingModule)
    },
    {
        path: 'esign',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/esign/esign.module').then((m) => m.EsignModule)
    },
    {
        path: 'facility-contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/facility-contracts/facility-contracts.module').then((m) => m.FacilityContractsModule)
    },
    {
        path: 'travel-contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/travel-contracts/travel-contracts.module').then((m) => m.TravelContractsModule)
    },
    {
        path: 'documents',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/documents/documents.module').then((m) => m.DocumentsModule)
    },
    {
        path: 'margin-calculator',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/margin-calculator/margin-calculator.module').then((m) => m.MarginCalculatorModule)
    },
    {
        path: 'subcontractor-margin-calculator',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/subcontractor-margin-calculator/subcontractor-margin-calculator.module').then(
                (m) => m.SubcontractorMarginCalculatorModule
            )
    },
    {
        path: 'payroll/facility-profiles',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/facility-profiles/facility-profiles.module').then((m) => m.FacilityProfilesModule)
    },
    {
        path: 'account-receivables/facilities',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/account-receivables/account-receivables.module').then((m) => m.AccountReceivablesModule)
    },
    {
        path: 'account-receivables/contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/account-receivables/contracs-grid/contracts.module').then((m) => m.ContractsModule)
    },
    {
        path: 'payroll/facility-profiles/billing-groups',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/billing-groups/billing-group.module').then((m) => m.BillingGroupModule)
    },
    {
        path: 'payroll/contracts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/payroll/search-contracts/search-contracts.module').then(
                (m) => m.PayrollSearchContractsModule
            )
    },
    {
        path: 'payroll/timecard-shifts',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/payroll/timecard-shifts/timecard-shifts.module').then((m) => m.TimecardShiftsModule)
    },
    {
        path: 'payroll',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/payroll/payroll.module').then((m) => m.PayrollModule)
    },
    {
        path: 'leadqueue',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/lead-queue/lead-queue.routes').then((m) => m.LeadQueueRoutes)
    },
    {
        path: 'clinical',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/clinical/clinical.module').then((m) => m.ClinicalModule)
    },
    {
        path: 'locums',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/locums/locums.routes').then((m) => m.ModuleRoutes)
    },
    {
        path: 'recruiting',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/recruiting/recruiting.module').then((m) => m.RecruitingModule)
    },
    {
        path: 'travelx',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/travelx/travelx.routes').then((m) => m.TravelXRoutes)
    },
    {
        path: 'travelxnew',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/travelxnew/travelxnew.module').then((m) => m.TravelXNewModule)
    },
    {
        path: 'partnernetwork',
        canActivate: [AuthGuard],
        providers: [...PARTNER_NETWORK_PROVIDERS],
        loadChildren: () =>
            import('src/app/partner-network/partner-network-routing.module').then((m) => m.PARTNER_NETWORK_ROUTES)
    },
    {
        path: 'perdiem',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/perdiem/perdiem.module').then((m) => m.PerdiemModule)
    },
    {
        path: 'commissions',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/commissions/commissions.module').then((m) => m.CommissionsModule)
    },
    {
        path: 'corporate-directory',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/corporate-directory/corporate-directory.routes').then((m) => m.CorporateDirectoryRoutes)
    },
    {
        path: 'reporting',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/reporting/reporting.routes').then((m) => m.ReportingRoutes)
    },
    {
        path: 'perdiem-recruiting',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/perdiem-recruiting/perdiem-recruiting.module').then((m) => m.PerDiemRecruitingModule)
    },
    {
        path: 'staff-recruiting',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/perm-recruiting/perm-recruiting.module').then((m) => m.PermRecruitingModule)
    },
    {
        path: 'housing',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/housing/housing.routes').then((m) => m.HousingRoutes)
    },
    {
        path: 'vendors',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/housing/vendors/vendors.routes').then((m) => m.VendorRoutes)
    },
    {
        path: 'references',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/references/references.module').then((m) => m.ReferencesModule)
    },
    {
        path: 'martech',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/martech/martech.routes').then((m) => m.martechModuleRoutes)
    },

    {
        path: 'workforce-solutions',
        canActivate: [AuthGuard],
        loadChildren: () =>
            import('src/app/workforce-solutions/workforce-solutions.module').then((m) => m.WorkforceSolutionsModule)
    },
    {
        path: 'access-control',
        canActivate: [AuthGuard],
        loadChildren: () => import('src/app/access-control/access-control.module').then((m) => m.AccessControlModule)
    },
    {
        path: 'devops',
        loadChildren: () => import('src/app/devops/devops.routes').then((m) => m.DevOpsModuleRoutes)
    },
    {
        path: 'signin',
        loadChildren: () => import('src/app/signin/signin.routes').then((m) => m.SigninRoutes)
    },
    {
        path: 'sso',
        loadChildren: () => import('src/app/signin/signin.routes').then((m) => m.SigninRoutes)
    },
    {
        path: 'logout',
        loadChildren: () => import('src/app/signin/signin.routes').then((m) => m.SigninRoutes)
    },
    {
        path: '',
        redirectTo: '/welcome/home',
        pathMatch: 'full'
    },
    {
        path: '**',
        component: CatchAllComponent
    }
    // /*wait until esnext is on*/ () => import('src/app/travelx/travelx.module').then(module => module.TravelXModule)}
];

@NgModule({
    imports: [
        RouterModule.forRoot(appRoutes, {
            useHash: true,
            initialNavigation: 'enabledNonBlocking',
            enableTracing: false,
            paramsInheritanceStrategy: 'always'
        })
    ],
    exports: [RouterModule],
    providers: [
        { provide: RouteReuseStrategy, useClass: MasterDetailRouteReuseStrategy },
        { provide: TitleStrategy, useClass: CustomTitleStrategy }
    ]
})
export class AppRoutingModule {}
